/**
 * UTILITAIRES DE FORMATAGE - Réutilisables dans toute l'application
 * Centralise toutes les fonctions de formatage pour une maintenance facile
 */

/**
 * Formate un montant en milliers avec séparateur de milliers français
 * @param {number} amount - Montant à formater
 * @returns {string} Montant formaté en milliers (ex: "6 500" pour 6500000)
 * @example
 * formatAmountInThousands(6500000) // "6 500"
 * formatAmountInThousands(1200) // "1"
 * formatAmountInThousands(null) // "0"
 */
/**
 * Formate un montant en milliers (K) ou millions (M) automatiquement
 * @param {number|string} amount - Le montant à formater
 * @returns {string} Montant formaté avec suffixe K ou M
 */
export const formatAmountInThousands = (amount, currency = "Fcfa", decimals = 1) => {
  // Gestion robuste des valeurs nulles, undefined ou non numériques
  if (amount === null || amount === undefined || amount === "" || isNaN(Number(amount))) {
    return `0 ${currency}`;
  }
  
  const numericAmount = Number(amount);
  
  if (numericAmount === 0) return `0 ${currency}`;

  // Décision automatique selon la valeur avec séparateurs de milliers
  if (Math.abs(numericAmount) >= 1000000000) {
    // Format en milliards
    const amountInBillions = numericAmount / 1000000000;
    const formatted = amountInBillions.toLocaleString('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals
    });
    return `${formatted} Md ${currency}`;
  } else if (Math.abs(numericAmount) >= 1000000) {
    // Format en millions
    const amountInMillions = numericAmount / 1000000;
    const formatted = amountInMillions.toLocaleString('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals
    });
    return `${formatted} M ${currency}`;
  } else if (Math.abs(numericAmount) >= 1000) {
    // Format en milliers
    const amountInThousands = numericAmount / 1000;
    const formatted = amountInThousands.toLocaleString('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals
    });
    return `${formatted} k ${currency}`;
  } else {
    // Format normal avec séparateurs de milliers et gestion des décimales
    return `${numericAmount.toLocaleString('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: numericAmount % 1 === 0 ? 0 : 2
    })} ${currency}`;
  }
};


export const formatNumber = num =>
    num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ") || "0";

/**
 * Formate une date au format français court (ex: "15 janv. 2024")
 * @param {string} dateString - Date au format string
 * @returns {string} Date formatée ou "-" si date invalide
 */
export const formatDate = (dateString) => {
  if (!dateString) return "-";
  
  try {
    const date = new Date(dateString);
    // Options pour un format court et lisible
    const options = { day: "numeric", month: "short", year: "numeric" };
    return date.toLocaleDateString("fr-FR", options);
  } catch (error) {
    // console.warn("Erreur de formatage de date:", error);
    return "-";
  }
};


/**
 * Détermine le statut affichable d'un événement basé sur son statut technique
 * @param {Object} event - Objet événement contenant un champ status
 * @returns {string} Statut formaté pour l'affichage
 */
export const getEventStatus = (event) => {
  const status = event.status?.toLowerCase() || "pending";
  
  switch (status) {
    case "processed": return "Processed";
    case "cancelled": return "Cancelled";
    case "partially": return "Partially";
    default: return "Pending";
  }
};

/**
 * Retourne les classes CSS Bootstrap pour le badge de statut
 * @param {string} status - Statut de l'événement
 * @returns {string} Classes CSS pour le badge
 */
export const getBadgeClass = (status) => {
  const baseClass = "badge rounded-pill border border-opacity-25";
  
  switch (status) {
    case "Processed":
      return `${baseClass} bg-success bg-opacity-10 text-success border-success`;
    case "Pending":
      return `${baseClass} bg-warning bg-opacity-10 text-warning border-warning`;
    case "Cancelled":
      return `${baseClass} bg-danger bg-opacity-10 text-danger border-danger`;
    case "Partially":
      return `${baseClass} bg-info bg-opacity-10 text-info border-info`;
    default:
      return `${baseClass} bg-secondary text-white`;
  }
};

/**
 * Détermine le style de couleur pour une date selon son échéance
 * @param {string} dateString - Date à évaluer
 * @returns {Object} Style CSS (couleur rouge pour dépassé, vert pour futur)
 */
export const getDateStyle = (dateString) => {
  if (!dateString) return {};
  
  try {
    const planningDate = new Date(dateString);
    const today = new Date();
    
    // Tronquer les dates pour comparer seulement jour/mois/année
    const truncateToDate = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    const isToday = truncateToDate(planningDate).getTime() === truncateToDate(today).getTime();
    const isExpired = truncateToDate(planningDate) < truncateToDate(today);
    
    // Aujourd'hui: pas de style spécial
    if (isToday) return {};
    
    // Date passée: rouge, Date future: vert
    return isExpired ? { color: "red" } : { color: "green" };
    
  } catch (error) {
    // console.warn("Erreur d'analyse de date:", error);
    return {};
  }
};